import { Box, Button, Divider, Grid, MenuItem, Select, Stack, Typography } from "@mzc-pdc/ui";
import { IconAlternateEmail, IconReply, IconThread, ImageEmpty } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RandomColoredAvatar } from "@components_v2/avatar/random-colored-avatar";
import { useTranslation } from "react-i18next";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import WidgetWrap from "@components_v2/dashboard/ui/widget-wrapper";
import { getWidgetData } from "@modules/dashboard";
import { useDispatch, useSelector } from "react-redux";
import CommentDate from "@features/comments/comment-date";
import useUserDetail from "@hooks/useUserDetail";
import UserDetailPopper from "@features/user-detail-popper/user-detail-popper";

import useComments from "@hooks/useComments";
import { GroupTitleWithIcon } from "@features/comments/groups";
import WidgetHead from "@components_v2/dashboard/ui/widget-head";
import menuService from "@services/menuService";
import { useLocation, useHistory, Link } from "react-router-dom";
import { NoSearchResultsIcon } from "@features/no-search-results-icon";
import { alpha } from "@mui/material";
import { siteMode } from "@cores/siteMode";
import Tooltip from "@components_v2/Tooltip/tooltip";
import useSettings from "@hooks/useSettings";
import { CUSTOM_MENU_DOMAIN, MENU_LIST } from "@constants";
import useCustomMenus from "@hooks/useCustomMenus";
import EllipsisTooltip from "@components_v2/ellipsis-tooltip";

const RecentComments = ({ id, name, title, icon, color, onMove, onDelete, fullWidth, blockDrag, isCustomMenu }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const history = useHistory();
    const { convertCommentToHTML } = useComments({});
    const {
        handleUserProfileClick,
        handleUserProfileClose,
        userProfileAnchorEl,
        openUserProfile,
        userProfileData,
        userProfilePending,
    } = useUserDetail();
    const { navigatorMenus } = useSettings({ type: "General" });
    const { hasMenuWithDomain } = useCustomMenus();

    const { spaceId, projectId } = menuService.parse(pathname);
    const isLibrary = siteMode.get() === "CMS";

    const COMMENT_TYPES = [
        {
            value: "THREAD_I_BELONG_TO",
            label: t(`common::label::Comments in threads I belong to`),
            icon: <IconAlternateEmail size={16} />,
        },
        {
            value: "MENTIONED_TO_ME",
            label: t(`common::label::Comments where I was mentioned`),
            icon: <IconAlternateEmail size={16} />,
        },
        {
            value: "CREATED_BY_ME",
            label: t(`common::label::Comments written by me`),
            icon: <IconReply size={16} />,
        },
        {
            value: "REPLIED_TO_ME",
            label: t(`common::label::Reply to my comment`),
            icon: <IconThread size={16} />,
        },
    ];

    const { widgetData } = useSelector(({ dashboard }) => ({
        widgetData: dashboard.dashboardWidgetData?.find((widget) => widget.id === id),
    }));
    const user = useSelector(({ stage }) => stage?.user);

    const [commentType, setCommentType] = useState(COMMENT_TYPES[0].value);

    const getCommentType = (value) => {
        const el = COMMENT_TYPES.find((el) => el.value === value);
        return (
            <Stack direction={"row"} gap={1} alignItems={"center"}>
                {el?.icon}
                <Typography variant={"body1_500"}>{el.label}</Typography>
            </Stack>
        );
    };
    const onHandleChangeCommentType = (e) => {
        setCommentType(e.target.value);
    };

    const getAssetMediaType = useCallback((el) => {
        const mediaType = el.thread?.parentComment
            ? el.thread?.parentComment?.thread?.context?.data?.mediaType
            : el.thread?.context?.data?.mediaType;

        return mediaType;
    }, []);

    const getAssetName = useCallback((el) => {
        const mediaType = el.thread?.parentComment
            ? el.thread?.parentComment?.thread?.context?.data?.name
            : el.thread?.context?.data?.name;

        return mediaType;
    }, []);

    const getThreadName = useCallback((el) => {
        const threadName = el.thread?.parentComment ? el.thread?.parentComment?.thread?.name : el.thread?.name;

        return threadName;
    }, []);

    const getWidgetDetail = useCallback(() => {
        const params = { commentSearchMode: commentType, createdById: user?.id };
        dispatch(getWidgetData({ id, params }));
    }, [commentType, user, id, dispatch]);

    const getHtmlComments = useCallback(
        (el) => {
            // TODO: 말줄임 표시 추가해야함 첫째 문단만 보여주기
            return `<span class="cpm-dashboard-replied"><span class="cpm-dashboard-comment" style="flex:none; padding-right: 4px">${t(
                "common::label::Replied to :",
                "Replied to :",
            )}</span>${convertCommentToHTML(el, "cpm-dashboard-comment")}</span>`;
        },
        [convertCommentToHTML, t],
    );

    const getDestinationInfo = useCallback(
        (el) => {
            if (!el) {
                return { paths: menuService.paths, isLibrary: false, destinationThread: null, isSubThread: false };
            }

            const isSubThread = el.thread.context?.domain === "COMMENT";
            const destinationThread = isSubThread ? el.thread.parentComment?.thread?.context : el.thread.context;

            if (!destinationThread) {
                return { paths: menuService.paths, isLibrary: false, destinationThread: null, isSubThread };
            }

            let isLibraryPath = isLibrary;
            if (destinationThread.domain === "REQUEST") {
                const isMyRequest = destinationThread?.data?.created?.by?.id === user.id;
                isLibraryPath = isMyRequest && isLibrary;
            }

            const paths = isLibraryPath ? menuService.paths.library : menuService.paths;

            return { paths, isLibrary: isLibraryPath, destinationThread, isSubThread };
        },
        [user, isLibrary],
    );

    const userSiteAccess = useMemo(
        () => user.projects?.find((project) => project.id === projectId)?.siteAccess,
        [user, projectId],
    );

    const getCommentDestinationPage = useCallback(
        (el, { paths: menuServicePaths, destinationThread, isSubThread }) => {
            let domainPath = "";

            if (!el || !destinationThread) return "/";

            const destinationDomain = destinationThread.domain;

            switch (destinationThread.domain) {
                case "ASSET":
                    if (
                        !navigatorMenus[MENU_LIST.ASSETS]?.visible ||
                        (isCustomMenu && !hasMenuWithDomain(CUSTOM_MENU_DOMAIN.ASSETS))
                    )
                        //Note: 에셋의 경우 커스텀 메뉴 사용시에도 이동은 가능하지만, 다른 도메인 댓글과의 일관된 동작을 위해서 이동 불가처리
                        return "";
                    if (isCustomMenu) {
                        domainPath = `${menuServicePaths.customMenuSets.assetsAssetDetail?.(
                            spaceId,
                            projectId,
                            destinationThread.resourceId,
                        )}?projectId=${projectId}&`;
                    } else {
                        domainPath = `${menuServicePaths.assetsAssetDetail(
                            spaceId,
                            projectId,
                            destinationThread.resourceId,
                        )}?projectId=${projectId}&`;
                    }
                    break;
                case "COLLECTION":
                    if (!navigatorMenus[MENU_LIST.COLLECTIONS]?.visible || isCustomMenu) return ""; //Note: Collection은 커스텀메뉴인 상태면 상세로 넘겨줄 라우팅 주소가 없기 때문에 메뉴를 사용가능해도 넘어가지 못하게 처리
                    domainPath = `${menuServicePaths.assetsCollectionDetail(
                        spaceId,
                        projectId,
                        destinationThread.resourceId,
                    )}?`;
                    break;
                case "CUSTOM_CONTENT": {
                    if (!navigatorMenus[MENU_LIST.CUSTOM_CONTENTS]?.visible) return "";
                    const schemaId = destinationThread.resourceId.split(":")[0];
                    const contentId = destinationThread.resourceId.split(":")[1];
                    domainPath = `${menuServicePaths.contentsCustomContentDetail?.(
                        spaceId,
                        projectId,
                        schemaId,
                        contentId,
                    )}?`;
                    break;
                }
                case "PROJECT":
                    if (!navigatorMenus[MENU_LIST.DASHBOARD]?.visible) return "";
                    if (isCustomMenu) {
                        domainPath = `${pathname}?`;
                    } else {
                        domainPath = `${menuServicePaths.dashboard(spaceId, destinationThread.resourceId)}?`;
                    }
                    break;
                case "REQUEST": {
                    const isMyRequest = destinationThread?.data?.created?.by?.id === user.id;
                    if (isLibrary && !isMyRequest && !userSiteAccess.includes("CONSOLE")) return ""; //Note: 이전에 콘솔에서 리퀘스트 관련 멘션을 받은 사용자가 콘솔 siteAccess가 빠진 경우 접근 불가
                    domainPath = `${menuServicePaths.requestDetail(
                        spaceId,
                        projectId,
                        destinationThread.resourceId,
                    )}?requestProjectId=${projectId}&`;
                    break;
                }
            }
            return `${domainPath}threadId=${el.thread.id}&commentId=${el.id}&isSubThread=${
                isSubThread ? "true" : "false"
            }&domain=${destinationDomain}`; //여기 확인 후 수정
        },
        [
            spaceId,
            projectId,
            userSiteAccess,
            navigatorMenus,
            isCustomMenu,
            hasMenuWithDomain,
            isLibrary,
            user?.id,
            pathname,
        ],
    );

    useEffect(() => {
        getWidgetDetail();
    }, [commentType]);

    return (
        <WidgetWrap id={name} onMove={onMove} fullWidth={fullWidth} blockDrag={blockDrag}>
            <Grid container flexDirection={"column"} flex={1} height={"100%"}>
                <Grid item>
                    <WidgetHead
                        onRefresh={getWidgetDetail}
                        color={color}
                        icon={icon}
                        title={title}
                        actionMenus={[
                            {
                                skipTranslationLabel: t(`common::label::Remove`, "Remove"),
                                onClick: (e) => {
                                    onDelete(name);
                                },
                                enable: true,
                                buttonDisabled: true, //!removable,
                            },
                        ]}
                    />
                </Grid>
                <Grid item>
                    <Select
                        fullWidth
                        sx={{
                            height: theme.spacing(6),
                            "&:hover, &.Mui-focused": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: theme.palette.greySecondary[100],
                                },
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                                borderRadius: "0px",
                                borderTop: `1px solid ${theme.palette.greySecondary[100]}`,
                                borderBottom: `1px solid ${theme.palette.greySecondary[100]}`,
                            },
                        }}
                        renderValue={(selected) => getCommentType(selected)}
                        value={commentType}
                        onChange={onHandleChangeCommentType}
                    >
                        {COMMENT_TYPES.map((el) => (
                            <MenuItem
                                key={`recent-comments-${el.value}-${el.label}`}
                                value={el.value}
                                sx={{ height: "40px" }}
                            >
                                <Typography variant={"body1_500"}>{el.label}</Typography>
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
                {widgetData &&
                    (widgetData.pending ? (
                        <Grid item flex={1}>
                            <Grid container height={"100%"} justifyContent={"center"} alignItems={"center"}>
                                <span className={"loading"}>
                                    <i className={"loading-content"} />
                                </span>
                            </Grid>
                        </Grid>
                    ) : !widgetData.pending && (widgetData?.data ?? [])?.length === 0 ? (
                        <Grid item flex={1}>
                            <NoSearchResultsIcon
                                title={"There is no data for this widget."}
                                icon={<ImageEmpty width={80} height={72} sx={{ marginBottom: 2.5 }} />}
                            />
                        </Grid>
                    ) : (
                        <>
                            <Grid
                                item
                                flex={1}
                                width={1}
                                sx={{
                                    overflowY: "auto",
                                    "&::-webkit-scrollbar-track": {
                                        borderLeft: `1px solid ${theme.palette.divider}`,
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                    },
                                }}
                            >
                                {widgetData.data?.map((el, index) => {
                                    const destinationInfo = getDestinationInfo(el);
                                    const destinationPageUrl = getCommentDestinationPage(el, destinationInfo);
                                    const { isLibrary: isLibraryPath } = destinationInfo;
                                    const tooltipPhrase =
                                        destinationInfo.destinationThread.domain === "REQUEST"
                                            ? "You do not have permission to access the page."
                                            : "There is no page available for this comment.";

                                    return (
                                        <Stack
                                            key={el.id}
                                            {...(destinationPageUrl
                                                ? {
                                                      component: Link,
                                                      to: destinationPageUrl,
                                                      target: "_blank",
                                                  }
                                                : {})}
                                            direction={"column"}
                                            gap={theme.spacing(1.5)}
                                            px={theme.spacing(1.5)}
                                            py={theme.spacing(3)}
                                            overflow={"hidden"}
                                            borderBottom={
                                                index < widgetData.data?.length - 1
                                                    ? `1px solid ${theme.palette.greySecondary[100]}`
                                                    : "none"
                                            }
                                            sx={{
                                                cursor: destinationPageUrl ? "pointer" : "default",
                                                textDecoration: "none !important",
                                                "&:hover": {
                                                    backgroundColor: alpha(theme.palette.greySecondary[50], 0.24),
                                                },
                                            }}
                                        >
                                            <Tooltip
                                                placement={"top"}
                                                title={
                                                    !destinationPageUrl
                                                        ? t(`common::msg::${tooltipPhrase}`, tooltipPhrase)
                                                        : ""
                                                }
                                            >
                                                <Box component="span" sx={{ display: "block", width: "100%" }}>
                                                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                                                        <Box sx={{ height: "18px", minWidth: "20px" }}>
                                                            {getAssetMediaType(el) && (
                                                                <AssetTypeFlag
                                                                    size={"small"}
                                                                    type={getAssetMediaType(el)}
                                                                />
                                                            )}
                                                        </Box>

                                                        <EllipsisTooltip
                                                            text={getAssetName(el)}
                                                            sx={{
                                                                height: "100%",
                                                                display: "flex",
                                                                alignItems: "center",
                                                            }}
                                                            maxWidth={"83px"}
                                                            variant={"body3_500"}
                                                            typographyProps={{ color: theme.palette.primary.main }}
                                                        />

                                                        {el.thread?.type && (
                                                            <>
                                                                <Divider
                                                                    orientation={"vertical"}
                                                                    flexItem
                                                                    color={theme.palette.greySecondary[100]}
                                                                    sx={{ height: 18, mx: theme.spacing(1) }}
                                                                />

                                                                <Box
                                                                    sx={{
                                                                        height: "100%",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        mr: theme.spacing(1),
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant={"body3_500"}
                                                                        color={theme.palette.greySecondary[400]}
                                                                    >
                                                                        {isLibraryPath ? "Library" : "Console"}
                                                                    </Typography>
                                                                </Box>

                                                                <Stack sx={{ minWidth: 60, whiteSpace: "nowrap" }}>
                                                                    <GroupTitleWithIcon
                                                                        title={getThreadName(el)}
                                                                        isPublic={el.thread?.type === "PUBLIC"}
                                                                        iconSize={16}
                                                                        fontVariant={"body3_500"}
                                                                    />
                                                                </Stack>
                                                            </>
                                                        )}
                                                    </Stack>

                                                    <Grid container flexDirection={"row"} flexWrap={"nowrap"}>
                                                        <Grid
                                                            item
                                                            sx={{
                                                                flex: "0 0 24px",
                                                                mr: theme.spacing(1.5),
                                                                mt: theme.spacing(0.5),
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    width: "100%",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                <RandomColoredAvatar
                                                                    size={24}
                                                                    id={el.created?.by?.id}
                                                                    name={el.created?.by?.name}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            sx={{ display: "flex", flexDirection: "column", flex: 1 }}
                                                            zeroMinWidth
                                                        >
                                                            <>
                                                                <Grid
                                                                    item
                                                                    flexDirection={"row"}
                                                                    justifyContent={"space-between"}
                                                                    flexWrap={"nowrap"}
                                                                    sx={{
                                                                        display: "flex",
                                                                        width: "100%",
                                                                        flex: `0 0 18px`,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            width: "100%",
                                                                            alignItems: "center",
                                                                        }}
                                                                    >
                                                                        <>
                                                                            <Button
                                                                                variant={`solid`}
                                                                                size={"small"}
                                                                                color={"secondary"}
                                                                                onClick={(e) =>
                                                                                    handleUserProfileClick(
                                                                                        e,
                                                                                        el?.created?.by?.id,
                                                                                    )
                                                                                }
                                                                                sx={{ minWidth: 0, mr: 1.5 }}
                                                                            >
                                                                                <Typography
                                                                                    variant={`body1_700`}
                                                                                    component={"p"}
                                                                                >
                                                                                    {el.created?.by?.name}
                                                                                </Typography>
                                                                            </Button>
                                                                            <CommentDate
                                                                                date={el.created?.at}
                                                                                tooltipPlacement={"bottom"}
                                                                            />
                                                                        </>
                                                                    </Box>
                                                                </Grid>
                                                                {/*원본 댓글*/}
                                                                {el.thread?.parentComment && (
                                                                    <Grid
                                                                        item
                                                                        sx={{
                                                                            mt: 1,
                                                                        }}
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: getHtmlComments(
                                                                                el.thread?.parentComment,
                                                                            ),
                                                                        }}
                                                                    ></Grid>
                                                                )}
                                                                {/*대댓글*/}
                                                                <Grid
                                                                    item
                                                                    className="cpm-dashboard-comment-replied"
                                                                    sx={{
                                                                        width: "100%",
                                                                        flex: 1,
                                                                        mt: 1,
                                                                    }}
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: convertCommentToHTML(el),
                                                                    }}
                                                                ></Grid>
                                                            </>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Tooltip>
                                        </Stack>
                                    );
                                })}
                            </Grid>
                        </>
                    ))}
            </Grid>
            {!userProfilePending && (
                <UserDetailPopper
                    id={openUserProfile ? "user-profile-popover" : undefined}
                    handleUserProfileClose={handleUserProfileClose}
                    userProfileAnchorEl={userProfileAnchorEl}
                    openUserProfile={openUserProfile}
                    userProfileData={userProfileData}
                />
            )}
        </WidgetWrap>
    );
};

export default RecentComments;
